import Vector2 from './Vector2';
import Tool from './Tool';
import Rect from './Rect';
import MouseEventArgs, { MouseButton } from './MouseEventArgs';
import Editor from './Editor';
import ToolChain from './ToolChain';
import NodeLayer from './NodeLayer';

enum HandleType {
  BottomRight,
  BottomLeft,
  TopRight,
  TopLeft,
}

class TransformHandleTool extends Tool {
  private _type: HandleType;
  private _isDragging = false;
  private _isMouseOver = false;
  private _position = new Vector2();
  // private _handleRadius = 5 * window.devicePixelRatio;
  private _handleRadius = 12 * window.devicePixelRatio;
  private _initialRect = new Rect();
  private _initialMousePos = new Vector2();

  public constructor(editor: Editor, type: HandleType) {
    super(editor, 'transform_handle');
    this._type = type;
  }

  private hitTest(pos: Vector2): boolean {
    var selectedLayer = this.editor.selectedLayer;

    if (!(selectedLayer instanceof NodeLayer)) {
      return false;
    }

    var p1 = this.editor.camera.transformPointWorldToScreen(pos);
    var p2 = this.editor.camera.transformPointWorldToScreen(this._position);
    return p1.distanceTo(p2) < this._handleRadius;
  }

  private updatePosition() {
    var layer = this.editor.selectedLayer;

    if (layer == null) {
      return;
    }

    var rect = layer.boundingRect;

    if (this._type == HandleType.BottomRight) {
      this._position = rect.getEndPoint();
    }

    if (this._type == HandleType.BottomLeft) {
      this._position = rect.position.add(new Vector2(0, rect.size.y));
    }

    if (this._type == HandleType.TopRight) {
      this._position = rect.position.add(new Vector2(rect.size.x, 0));
    }

    if (this._type == HandleType.TopLeft) {
      this._position = rect.position;
    }
  }

  public handleMouseDown(arg: MouseEventArgs): boolean {
    if (arg.button != MouseButton.Left) {
      return false;
    }

    this.updatePosition();
    this._isDragging = this.hitTest(arg.worldPosition);

    if (this._isDragging) {
      this._initialRect = this.editor.selectedLayer.boundingRect;
      this._initialMousePos = arg.worldPosition;
    }
    return this._isDragging;
  }

  public handleMouseMove(arg: MouseEventArgs): boolean {
    this._isMouseOver = this.hitTest(arg.worldPosition);

    if (!this._isDragging) {
      return false;
    }

    var layer = this.editor.selectedLayer;
    var deltaPos = arg.worldPosition.sub(this._initialMousePos);

    var sizeLimit = (layer as NodeLayer).minSize;

    var newRect: Rect;

    if (this._type == HandleType.BottomRight) {
      var newSize = this._initialRect.size.add(deltaPos).limitToMin(sizeLimit);
      newRect = new Rect(this._initialRect.position, newSize);
    }

    if (this._type == HandleType.BottomLeft) {
      var newSize = new Vector2(
        this._initialRect.size.x - deltaPos.x,
        this._initialRect.size.y + deltaPos.y
      ).limitToMin(sizeLimit);
      var newPos = new Vector2(
        this._initialRect.position.x + this._initialRect.size.x - newSize.x,
        this._initialRect.position.y
      );
      newRect = new Rect(newPos, newSize);
    }

    if (this._type == HandleType.TopRight) {
      var newSize = new Vector2(
        this._initialRect.size.x + deltaPos.x,
        this._initialRect.size.y - deltaPos.y
      ).limitToMin(sizeLimit);
      var newPos = new Vector2(
        this._initialRect.position.x,
        this._initialRect.position.y + this._initialRect.size.y - newSize.y
      );
      newRect = new Rect(newPos, newSize);
    }

    if (this._type == HandleType.TopLeft) {
      var newSize = new Vector2(
        this._initialRect.size.x - deltaPos.x,
        this._initialRect.size.y - deltaPos.y
      ).limitToMin(sizeLimit);
      var newPos = new Vector2(
        this._initialRect.position.x + this._initialRect.size.x - newSize.x,
        this._initialRect.position.y + this._initialRect.size.y - newSize.y
      );
      newRect = new Rect(newPos, newSize);
    }

    newRect = new Rect(
      newRect.position,
      new Vector2(Math.max(newRect.size.x, 100), Math.max(newRect.size.y, 100))
    );

    layer.setBoundingRect(newRect);

    return true;
  }

  public handleMouseUp(arg: MouseEventArgs): boolean {
    if (this._isDragging) {
      this._isDragging = false;

      return true;
    }
    return false;
  }

  public draw(context: CanvasRenderingContext2D) {
    var selectedLayer = this.editor.selectedLayer;
    if (selectedLayer == null || !(selectedLayer instanceof NodeLayer)) {
      return;
    }

    this.updatePosition();

    var blue = 'rgb(155, 66, 227)';

    context.strokeStyle = blue;
    context.fillStyle = this._isMouseOver || this._isDragging ? blue : 'white';
    context.lineWidth = 2 * window.devicePixelRatio;

    var position = this.editor.camera.transformPointWorldToScreen(
      this._position
    );

    context.beginPath();
    context.arc(position.x, position.y, this._handleRadius, 0, 2 * Math.PI);
    context.fill();
    context.stroke();
  }
}

var handleEnums = [
  HandleType.BottomRight,
  HandleType.BottomLeft,
  HandleType.TopRight,
  HandleType.TopLeft,
];

function getTransformHandleArray(editor: Editor): Array<Tool> {
  var tools = new Array<Tool>();

  for (var i = 0; i < handleEnums.length; i++) {
    tools[i] = new TransformHandleTool(editor, handleEnums[i]);
  }

  return tools;
}

export default class ScaleTool extends ToolChain {
  public constructor(editor: Editor) {
    super(editor, 'scale', getTransformHandleArray(editor));
  }
}
